import { useQuery } from '@tanstack/react-query'

import { isApiError } from '@/api'
import { ErasureStatusResponse, ErasureStatusState } from '@/api/generated'
import { getMemberNotetakerErasureStatus } from '@/api/members'

const IN_FLIGHT_POLL_MS = 15_000

export const memberNotetakerErasureQueryKey = (userId: string) => ['team-members', 'notetaker-erasure', userId]

// A 4xx means the viewer may no longer read this member's status (role, billing or membership changed);
// a 429 is the status route's rate limiter, so it is transient like a 5xx.
export const isStatusRefusal = (error: unknown): boolean =>
  isApiError(error) && error.status >= 400 && error.status < 500 && error.status !== 429

// A failed poll keeps the last data, so a refusal must stop the poll; a 429, 5xx or network blip keeps it going.
export function nextStatusPoll(state: { data?: ErasureStatusResponse; error: unknown }): number | false {
  if (isStatusRefusal(state.error)) return false
  return state.data?.state === ErasureStatusState.InFlight ? IN_FLIGHT_POLL_MS : false
}

// Polls only while a run is in flight; a run lasts about 20 minutes (most of it the upload grace).
export function useMemberNotetakerErasure(userId: string) {
  return useQuery({
    queryKey: memberNotetakerErasureQueryKey(userId),
    queryFn: () => getMemberNotetakerErasureStatus(userId).then((r) => r.data),
    refetchInterval: (query) => nextStatusPoll(query.state),
  })
}
